/**
 * CD04 table-of-contents grouping and lock logic — pure, so it can be tested.
 *
 * These lived inside ClaudeDevV4TableComponent.js until CD04 briefly grew a second
 * shipping module (a Firebase one alongside a GitHub one, both gated on Module 4 so a
 * student could pick either). They moved out because that made the gating a product
 * decision worth testing, and the component cannot be imported under jsdom: it pulls in
 * UserContext, which pulls in `firebase/auth`, which fails to load.
 *
 * The parallel module is gone — Firebase won and became Module 5 — so every prerequisite
 * is once again simply the module before it. The indirection stays because it costs
 * nothing, it is covered by tests, and it is what let that experiment be added and
 * removed without touching a line of the locking below. Nothing here imports anything
 * but the registry.
 */
import { CLAUDE_DEV_V4_COURSE_LESSONS, V4_MODULES } from "./devV4LessonsRegistry";

/**
 * Group the flat registry into an array of module arrays, one per V4_MODULES entry and
 * in that order — so indices line up with V4_MODULES index-for-index, which
 * `applyCrossModuleUnlocks` relies on. Registry routes carry no leading slash, so
 * `onClick` prepends one.
 */
export function buildModules() {
    const groups = {};
    CLAUDE_DEV_V4_COURSE_LESSONS.forEach((l) => {
        const match = l.route.match(/V4_M(\d+)_/);
        const moduleNum = match ? parseInt(match[1], 10) : 1;
        if (!groups[moduleNum]) groups[moduleNum] = [];
        groups[moduleNum].push({
            title: l.title,
            lessonID: l.lessonID,
            completed: false,
            locked: true,
            onClick: `/${l.route}`,
        });
    });
    return V4_MODULES.map((d) => groups[d.moduleNum] || []);
}

/** Within a module, unlock completed lessons plus the first incomplete one; lock the rest. */
export function applySequentialUnlocksWithinModule(lessons) {
    let firstIncompleteUnlocked = false;
    return lessons.map((l) => {
        if (l.completed) {
            return { ...l, locked: false };
        }
        if (!firstIncompleteUnlocked) {
            firstIncompleteUnlocked = true;
            return { ...l, locked: false };
        }
        return { ...l, locked: true };
    });
}

/**
 * A module stays fully locked until its PREREQUISITE module is fully complete.
 *
 * V4_MODULES states that prerequisite explicitly rather than this assuming `i - 1`, so
 * two modules can share one — which is what let a second shipping module run beside
 * Module 5 without either hiding behind the other.
 */
export function applyCrossModuleUnlocks(modules) {
    const updated = modules.map((m) => [...m]);

    V4_MODULES.forEach((descriptor, i) => {
        if (descriptor.prereqModule == null) return;
        const prereqIdx = V4_MODULES.findIndex((d) => d.moduleNum === descriptor.prereqModule);
        if (prereqIdx === -1) return;

        // Read completion off the ORIGINAL array, not `updated`. Locking never changes
        // `completed`, so today the two agree — but reading the source keeps this
        // order-independent instead of quietly depending on V4_MODULES being sorted such
        // that a prerequisite is always visited first.
        const prereqDone = modules[prereqIdx].every((l) => l.completed);

        if (!prereqDone) {
            updated[i] = updated[i].map((l) => ({ ...l, locked: true }));
        } else {
            updated[i] = updated[i].map((l, idx) => (idx === 0 ? { ...l, locked: false } : l));
        }
    });
    return updated;
}

/** Apply within-module then cross-module gating to a set of module arrays. */
export function applyLocks(modules) {
    return applyCrossModuleUnlocks(modules.map(applySequentialUnlocksWithinModule));
}

/**
 * The module the learner is actually working through: the first one holding an
 * incomplete lesson, which the table of contents opens by default. Cross-module gating
 * means everything before it is finished, so this falls back to the last module once the
 * whole course is complete.
 */
export function findActiveModuleIndex(modules) {
    const idx = modules.findIndex((m) => m.some((l) => !l.completed));
    return idx === -1 ? Math.max(modules.length - 1, 0) : idx;
}
